import { QuoteType } from './SE.interfaces';

export const MilestoneCodes = {
  ALL: {
    label: 'All',
    key: 'ALL',
    countLabel: 'TOTAL_ACTIVE_COUNT',
    width: 50,
  },
  DRAFT: {
    label: 'Drafts',
    key: 'DRAFT',
    countLabel: 'DRAFT',
    width: 70,
  },
  AT_ORIGIN: {
    label: 'Origin',
    key: 'AT_ORIGIN',
    countLabel: 'AT_ORIGIN',
    width: 70,
  },
  IN_TRANSIT: {
    label: 'In Transit',
    key: 'IN_TRANSIT',
    countLabel: 'IN_TRANSIT',
    width: 90,
  },
  TRANSSHIPMENT: {
    label: 'Transhipment',
    key: 'TRANSSHIPMENT',
    countLabel: 'TRANSSHIPMENT',
    width: 110,
  },
  DESTINATION: {
    label: 'Destination',
    key: 'DESTINATION',
    width: 100,
    countLabel: 'DESTINATION',
  },
  COMPLETED: {
    label: 'Completed',
    key: 'COMPLETED',
    width: 90,
    countLabel: 'COMPLETED',
  },
  ON_HOLD: {
    label: 'On Hold',
    key: 'ON_HOLD',
    width: 80,
    countLabel: 'ON_HOLD',
  },
  DELETED: {
    label: 'Deleted',
    key: 'DELETED',
    width: 90,
    countLabel: 'DELETED',
  },
};

export const ValidMilestoneCodes = {
  AT_ORIGIN : 'AT_ORIGIN',
  DRAFT : 'DRAFT',
  IN_TRANSIT : 'IN_TRANSIT',
  TRANSSHIPMENT : 'TRANSSHIPMENT',
  DESTINATION : 'DESTINATION',
  COMPLETED : 'COMPLETED',
  ON_HOLD: 'ON_HOLD',
  DELETED: 'DELETED',
};

export const ValidTaskStatusCodes = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  DEADLINE_ELAPSED: 'DEADLINE_ELAPSED',
  DEADLINE_APPROACHING: 'DEADLINE_APPROACHING',
  OPEN: 'OPEN',
};

export const ValidMilestoneAndStatusCodes = {
  ...ValidMilestoneCodes,
  ...ValidTaskStatusCodes,
};

export const SESortOptions = {
  nearest_deadline: 'Nearest Deadline',
  updated_at: 'Last Activity Date',
  created_at: 'Recently Created',
};

export const SEDateFilterOptions = {
  nearestDeadline: 'Nearest Task Deadline',
  updatedAt: 'Last Update',
  createdAt: 'Creation Date',
}

export const RelianceCompanyCodeMapping = [
  { key: 'RJIL', label: 'RJIL' },
  { key: 'RPPMSL', label: 'RPPMSL' },
  { key: 'JPL', label: 'JPL' },
  { key: 'RBL', label: 'RBL' },
];

export enum ProcurementType {
  SPOT = 'SPOT',
  CONTRACT = 'CONTRACT',
}

export const ProcurementTypeLabels: Record<string, string> = {
  [ProcurementType.SPOT]: 'Spot',
  [ProcurementType.CONTRACT]: 'Tender',
};

/**
 * Field-type string for each Quote Details field in the Quotes Selection task (SMT71).
 * Matches the backend FieldValueType.RFQ_QUOTE; routed in SEFieldGroupUtils.
 */
export const RFQ_QUOTE_FIELD_TYPE = 'RFQ_QUOTE';

/** True if a task-details field is a per-vendor Quote Details field (renders as a tab in SMT71). */
export const isRfqQuoteField = (field?: { type?: string }): boolean =>
  field?.type === RFQ_QUOTE_FIELD_TYPE;

/**
 * The Quotes Selection task (SMT71) is identified by the presence of ANY RFQ_QUOTE field in the
 * task-details payload — not by field order, which the backend does not guarantee.
 */
export const isQuoteSelectionTaskFields = (taskFields?: { type?: string }[]): boolean =>
  !!taskFields?.some(isRfqQuoteField);

/**
 * Master codes for the non-quote summary fields on the Quotes Selection task (SMT71). Their values are
 * derived by the frontend (totals computed from selected quotes; reason/remarks captured in the
 * deviation popup) and injected into the submit payload rather than rendered as inline form fields.
 */
export const QUOTE_TOTAL_AMOUNT_CODE = 'SMF423';
export const QUOTE_TOTAL_L1_DEVIATION_CODE = 'SMF424';
export const QUOTE_L1_DEVIATION_REASON_CODE = 'SMF425';
export const QUOTE_DEVIATION_REMARKS_CODE = 'SMF426';

/**
 * Quote Candidates (SMF427) — the full ranked list shown to the ops user at selection time, across
 * ALL included vendor types, stored in a single MULTIPLE RFQ_QUOTE field. The frontend snapshots the
 * displayed quotes here on SMT71 submit so the L1-deviation approver (SMT72) sees the exact list the
 * ops user chose from. Unlike SMF416/417/418 it is NOT a per-vendor selectable tab.
 */
export const QUOTE_CANDIDATES_CODE = 'SMF427';

/**
 * True for the Quote Candidates snapshot field (SMF427) — the full list, not a per-vendor selection.
 * Identified by master code when present, else by being the sole MULTIPLE RFQ_QUOTE field (the
 * per-vendor Quote Details fields are all SINGLE) — the backend does not always populate `code`.
 */
export const isQuoteCandidatesField = (field?: {
  code?: string;
  masterCode?: string;
  type?: string;
  isMultipleEntryType?: boolean;
}): boolean =>
  (field?.masterCode || field?.code) === QUOTE_CANDIDATES_CODE ||
  (isRfqQuoteField(field) && field?.isMultipleEntryType === true);

/**
 * A per-vendor Quote Details field (SMF416/417/418) — an RFQ_QUOTE field the ops user selects one
 * quote from, rendered as a tab. Excludes the Quote Candidates snapshot (SMF427).
 */
export const isPerVendorQuoteField = (field?: {
  type?: string;
  code?: string;
  masterCode?: string;
  isMultipleEntryType?: boolean;
}): boolean => isRfqQuoteField(field) && !isQuoteCandidatesField(field);

/**
 * Vendor types for quote search. The enum values are the `vendorType` query-param sent to
 * `execution/:id/quote-search` and MUST match the backend ORGANISATION_TYPES enum.
 */
export enum VendorType {
  FF = 'FF',
  SHIPPING_LINE = 'SHIPPING_LINE',
  CHA = 'CHA',
  CFS_CONTRACTOR = 'CFS_CONTRACTOR',
  ICD_CONTRACTOR = 'ICD_CONTRACTOR',
  SURVEYOR = 'SURVEYOR',
  TRANSPORTER = 'TRANSPORTER',
  BREAK_BULK = 'BREAK_BULK',
  INTER_CARTING = 'INTER_CARTING',
}

export const VendorTypeLabels: Record<string, string> = {
  [VendorType.FF]: 'Freight Forwarder',
  [VendorType.SHIPPING_LINE]: 'Shipping Line',
  [VendorType.CHA]: 'CHA',
  [VendorType.CFS_CONTRACTOR]: 'CFS Contractor',
  [VendorType.ICD_CONTRACTOR]: 'ICD Contractor',
  [VendorType.SURVEYOR]: 'Surveyor',
  [VendorType.TRANSPORTER]: 'Transporter',
  [VendorType.BREAK_BULK]: 'Break Bulk',
  [VendorType.INTER_CARTING]: 'Inter Carting',
};

/**
 * Backend field master codes for the Quote Details fields (SMT71), mapped to their vendor type.
 * Master code is the reliable identifier (present on the task-details field payload); the field name
 * is only a fallback. Only CHA / CFS_CONTRACTOR / ICD_CONTRACTOR are in scope for quote selection today.
 */
export const QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE: Record<string, VendorType> = {
  SMF416: VendorType.CHA,
  SMF417: VendorType.CFS_CONTRACTOR,
  SMF418: VendorType.ICD_CONTRACTOR,
};

/**
 * Derives the vendor type of a Quote Details field. Prefers the master code (SMF416/417/418), falling
 * back to matching the field name ("Quote Details — CHA / CFS Contractor / ICD Contractor").
 * `field` is the task-details field payload (extraData) — it carries masterCode/code and name.
 */
export const getVendorTypeFromQuoteField = (field?: {
  masterCode?: string;
  code?: string;
  name?: string;
}): VendorType | undefined => {
  const masterCode = field?.masterCode || field?.code;
  if (masterCode && QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE[masterCode]) {
    return QUOTE_DETAILS_MASTER_CODE_TO_VENDOR_TYPE[masterCode];
  }
  const name = field?.name;
  if (isCfsContractorField(name)) return VendorType.CFS_CONTRACTOR;
  if (isIcdContractorField(name)) return VendorType.ICD_CONTRACTOR;
  if (isChaContractorField(name)) return VendorType.CHA;
  return undefined;
};

export interface QuoteTotals {
  totalQuoteAmount: number;
  totalDeviation: number;
}

/**
 * Sums the selected quotes into the SMT71 summary totals. Both are read from the INR-normalised
 * comparable (executionRanking.inr_value) — the backend persists SMF423/SMF424 in INR and the value
 * is currency-agnostic, so quotes in different currencies still total correctly. Shared by the live
 * footer and the submit payload so the displayed and persisted totals cannot drift.
 */
export const getQuoteTotals = (quotes: QuoteType[]): QuoteTotals => {
  const totalQuoteAmount = quotes.reduce(
    (sum, quote) => sum + (quote.executionRanking?.inr_value || 0),
    0,
  );
  const totalDeviation = quotes.reduce((sum, quote) => sum + (Number(quote.deviationFromL1) || 0), 0);
  return { totalQuoteAmount, totalDeviation };
};

/**
 * SMT70 — Vendor Type Selection.
 * The container-handling sub-fields are identified by their FieldValueType (unique to this task),
 * so the conditional-visibility logic auto-scopes to SMT70 without needing the task master code.
 */
export const CHECKBOX_FIELD_TYPE = 'CHECKBOX';

/**
 * Buffer Days are split into two vendor-specific fields with distinct FieldValueTypes; their option
 * sets (CFS -> 7/14/21, ICD -> 10/21) are served by the generic dropdown endpoint keyed on the type.
 */
export const CFS_BUFFER_DAYS_FIELD_TYPE = 'CFS_BUFFER_DAYS';
export const ICD_BUFFER_DAYS_FIELD_TYPE = 'ICD_BUFFER_DAYS';

/** Sub-fields revealed only when CFS Contractor is selected. */
export const CfsOnlyContainerSubFieldTypes = [
  'SEGMENT',
  'PANEL_IDENTIFIER',
  CFS_BUFFER_DAYS_FIELD_TYPE,
];

/** Sub-fields revealed only when ICD Contractor is selected. */
export const IcdOnlyContainerSubFieldTypes = [
  ICD_BUFFER_DAYS_FIELD_TYPE,
];

/** Sub-fields revealed when either CFS or ICD Contractor is selected (Cargo Type, Gauge Status & Destuff Indicator apply to both). */
export const SharedContainerSubFieldTypes = ['CARGO_TYPE', 'GAUGE_STATUS', 'DESTUFF_INDICATOR'];

/** Sub-fields revealed only when CHA is selected (Material Category, Plant Type). */
export const ChaSubFieldTypes = ['MATERIAL_CATEGORY', 'PLANT_TYPE'];

/** Sub-fields revealed only when Surveyor is selected (Surveyor Type: MWS / Bulk). */
export const SurveyorSubFieldTypes = ['SURVEYOR_TYPE'];

export const AllContainerSubFieldTypes = [
  ...CfsOnlyContainerSubFieldTypes,
  ...IcdOnlyContainerSubFieldTypes,
  ...SharedContainerSubFieldTypes,
];

/**
 * The vendor checkboxes share FieldValueType `CHECKBOX`, and the task-details field response carries
 * no master code — so they are matched by name (which contains "CFS" / "ICD" / "CHA" / "Surveyor").
 */
export const isCfsContractorField = (name?: string): boolean => /\bcfs\b/i.test(name || '');
export const isIcdContractorField = (name?: string): boolean => /\bicd\b/i.test(name || '');
export const isChaContractorField = (name?: string): boolean => /\bcha\b/i.test(name || '');
export const isSurveyorField = (name?: string): boolean => /surveyor/i.test(name || '');
export const isFreightForwarderField = (name?: string): boolean => /freight\s*forwarder/i.test(name || '');
export const isShippingLineField = (name?: string): boolean => /shipping\s*line/i.test(name || '');
export const isBreakBulkVendorField = (name?: string): boolean => /break\s*bulk/i.test(name || '');

/**
 * Mode-driven vendor-type visibility for SMT70:
 *  - Break Bulk mode  -> hide Freight Forwarder + Shipping Line, show Break Bulk Vendor
 *  - FCL / LCL / Air  -> show Freight Forwarder + Shipping Line, hide Break Bulk Vendor
 */
export const isBreakBulkMode = (mode?: string): boolean =>
  (mode || '').toUpperCase() === 'BREAKBULK';

export const myTaskListStatus = [
  'Done',
  'In Progress',
  'Cancelled',
  'Not Started',
  'Sent For Approval',
  'Rejected',
  'Amend Task',
];

export const taskResourceTypes = {
  DOCUMENTS: 'DOCUMENTS',
  CONTAINERS: 'CONTAINERS',
  AWBS: 'AWBS'
}

export const sortOrderTypes ={
  ASCENDING :'ASC',
  DESCENDING :'DESC'
}
export const seachFilterOptions = [
  { key: 'ALL', label: 'All' },
  { key: 'SHIPMENT #', label: 'Shipment Number' },
  { key: 'BOOKING #', label: 'Booking Number' },
  { key: 'CLIENT REF #', label: 'Client Reference Number' },
  { key: 'SALES ORDER #', label: 'Sales Order Number' },
  { key: 'PURCHASE ORDER #', label: 'Purchase Order Number'},
  { key: 'INVOICE #', label: 'Invoice Number'},
  { key: 'CUSTOM FIELDS', label: 'Custom Fields'},
];

export const bulkUploadTabs = {
  'PIC_ASSIGNMENT': 'PIC Assignment',
  'CONTAINER_FIELDS': 'Container Fields',
}

export const bulkUpdatesearchFilterOptions = [
  { key: 'CLIENT REF #', label: 'Internal Reference Number' },
  { key: 'SHIPMENT #', label: 'Shipsy Reference Number' },
  { key: 'SALES ORDER #', label: 'SO Number' },
  { key: 'PURCHASE ORDER #', label: 'PO Number'},
]

export const MAX_PIC_ASSIGNMENTS = 10;